import { BaseObj } from '@/objects/BaseObj';
import { useBaseObjEntityStateHandler } from '@/objects/entitystatehandler/entitystatehandler';
import { isRef, ref, unref } from 'vue';
import type { EntityState } from './../enums/EntityState';
import { formatDateIso } from '@/utilities/dateutils';

export class EventinfoSearchlistItem extends BaseObj {
    public ID: number;
    public __id: number;

    public ContactEmail: string;
    public __contactemail: string;

    public ContactPerson: string;
    public __contactperson: string;

    public ContactPhone: string;
    public __contactphone: string;

    public CreatedBy: string;
    public __createdby: string;

    public Date: Date;
    public __date: Date;

    public DisciplineName: string;
    public __disciplinename: string;

    public readonly EventID: number;

    public FederationName: string;
    public __federationname: string;

    public FirstStart: Date;
    public __firststart: Date;

    public HasResult: boolean;
    public __hasresult: boolean;

    public Name: string;
    public __name: string;

    public NumContestants: number;
    public __numcontestants: number;

    public OrganizerName: string;
    public __organizername: string;

    public SportlevelName: string;
    public __sportlevelname: string;

    public SportName: string;
    public __sportname: string;

    public __entityStateHandler;
    public __selfEntityState: EntityState;
    public __entityState: any;
    public get EntityState(): EntityState {
        return unref(this.__entityState);
    }
    /***
     * @deprecated Use the static FromJsValues method instead of the constructor to get type safety and default values
     */
    public constructor(values: {
        ID?: number;
        ContactEmail?: string | null;
        ContactPerson?: string | null;
        ContactPhone?: string | null;
        CreatedBy?: string | null;
        Date?: Date;
        DisciplineName?: string | null;
        EntityState?: EntityState;
        EventID?: number;
        FederationName?: string | null;
        FirstStart?: Date;
        HasResult?: boolean;
        Name?: string | null;
        NumContestants?: number;
        OrganizerName?: string | null;
        SportlevelName?: string | null;
        SportName?: string | null;
    }) {
        super();
        this.__entityState = ref(values.EntityState ?? 'Unchanged');
        this.__selfEntityState = values.EntityState ?? 'Unchanged';
        this.__entityStateHandler = useBaseObjEntityStateHandler<EventinfoSearchlistItem>(unref(this.__entityState), (states) => {
            if (isRef(this.__entityState)) {
                this.__entityState.value = states.EntityStateWithChildrenConsidered;
            } else {
                console.warn('EventinfoSearchlistItem tried to set entitystate, but entitystate is not a ref! This should never happen');
            }
            this.__selfEntityState = states.SelfEntityState;
        });
        this.ID = values.ID ?? 0;
        this.__id = values.ID ?? 0;
        Object.defineProperty(this, 'ID', {
            get(): number {
                return this.__id;
            },
            enumerable: true,
            configurable: true,
        });

        this.ContactEmail = values.ContactEmail ?? '';
        this.__contactemail = values.ContactEmail ?? '';
        Object.defineProperty(this, 'ContactEmail', {
            get(): string {
                return this.__contactemail;
            },
            set(value: string) {
                this.__entityStateHandler.propertyChanged('ContactEmail', this.__contactemail, value);
                this.__contactemail = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.ContactPerson = values.ContactPerson ?? '';
        this.__contactperson = values.ContactPerson ?? '';
        Object.defineProperty(this, 'ContactPerson', {
            get(): string {
                return this.__contactperson;
            },
            set(value: string) {
                this.__entityStateHandler.propertyChanged('ContactPerson', this.__contactperson, value);
                this.__contactperson = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.ContactPhone = values.ContactPhone ?? '';
        this.__contactphone = values.ContactPhone ?? '';
        Object.defineProperty(this, 'ContactPhone', {
            get(): string {
                return this.__contactphone;
            },
            set(value: string) {
                this.__entityStateHandler.propertyChanged('ContactPhone', this.__contactphone, value);
                this.__contactphone = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.CreatedBy = values.CreatedBy ?? '';
        this.__createdby = values.CreatedBy ?? '';
        Object.defineProperty(this, 'CreatedBy', {
            get(): string {
                return this.__createdby;
            },
            set(value: string) {
                this.__entityStateHandler.propertyChanged('CreatedBy', this.__createdby, value);
                this.__createdby = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.Date = values.Date ?? new Date('1970-01-01T00:00:00.000');
        this.__date = values.Date ?? new Date('1970-01-01T00:00:00.000');
        Object.defineProperty(this, 'Date', {
            get(): Date {
                return this.__date;
            },
            set(value: Date) {
                this.__entityStateHandler.propertyChanged('Date', this.__date, value);
                this.__date = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.DisciplineName = values.DisciplineName ?? '';
        this.__disciplinename = values.DisciplineName ?? '';
        Object.defineProperty(this, 'DisciplineName', {
            get(): string {
                return this.__disciplinename;
            },
            set(value: string) {
                this.__entityStateHandler.propertyChanged('DisciplineName', this.__disciplinename, value);
                this.__disciplinename = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.EventID = values.EventID ?? 0;

        this.FederationName = values.FederationName ?? '';
        this.__federationname = values.FederationName ?? '';
        Object.defineProperty(this, 'FederationName', {
            get(): string {
                return this.__federationname;
            },
            set(value: string) {
                this.__entityStateHandler.propertyChanged('FederationName', this.__federationname, value);
                this.__federationname = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.FirstStart = values.FirstStart ?? new Date('1970-01-01T00:00:00.000');
        this.__firststart = values.FirstStart ?? new Date('1970-01-01T00:00:00.000');
        Object.defineProperty(this, 'FirstStart', {
            get(): Date {
                return this.__firststart;
            },
            set(value: Date) {
                this.__entityStateHandler.propertyChanged('FirstStart', this.__firststart, value);
                this.__firststart = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.HasResult = values.HasResult ?? false;
        this.__hasresult = values.HasResult ?? false;
        Object.defineProperty(this, 'HasResult', {
            get(): boolean {
                return this.__hasresult;
            },
            set(value: boolean) {
                this.__entityStateHandler.propertyChanged('HasResult', this.__hasresult, value);
                this.__hasresult = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.Name = values.Name ?? '';
        this.__name = values.Name ?? '';
        Object.defineProperty(this, 'Name', {
            get(): string {
                return this.__name;
            },
            set(value: string) {
                this.__entityStateHandler.propertyChanged('Name', this.__name, value);
                this.__name = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.NumContestants = values.NumContestants ?? 0;
        this.__numcontestants = values.NumContestants ?? 0;
        Object.defineProperty(this, 'NumContestants', {
            get(): number {
                return this.__numcontestants;
            },
            set(value: number) {
                this.__entityStateHandler.propertyChanged('NumContestants', this.__numcontestants, value);
                this.__numcontestants = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.OrganizerName = values.OrganizerName ?? '';
        this.__organizername = values.OrganizerName ?? '';
        Object.defineProperty(this, 'OrganizerName', {
            get(): string {
                return this.__organizername;
            },
            set(value: string) {
                this.__entityStateHandler.propertyChanged('OrganizerName', this.__organizername, value);
                this.__organizername = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.SportlevelName = values.SportlevelName ?? '';
        this.__sportlevelname = values.SportlevelName ?? '';
        Object.defineProperty(this, 'SportlevelName', {
            get(): string {
                return this.__sportlevelname;
            },
            set(value: string) {
                this.__entityStateHandler.propertyChanged('SportlevelName', this.__sportlevelname, value);
                this.__sportlevelname = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.SportName = values.SportName ?? '';
        this.__sportname = values.SportName ?? '';
        Object.defineProperty(this, 'SportName', {
            get(): string {
                return this.__sportname;
            },
            set(value: string) {
                this.__entityStateHandler.propertyChanged('SportName', this.__sportname, value);
                this.__sportname = value;
            },
            enumerable: true,
            configurable: true,
        });
    }

    public _setNotifyParentOfModificationCallback(callback: () => void) {
        this.__entityStateHandler.setNotifyParentOfModificationCallback(callback);
    }

    public serialize(): string {
        return (
            '{' +
            `"ID": ${JSON.stringify(this.ID)}` +
            ',' +
            `"ContactEmail": ${JSON.stringify(this.ContactEmail)}` +
            ',' +
            `"ContactPerson": ${JSON.stringify(this.ContactPerson)}` +
            ',' +
            `"ContactPhone": ${JSON.stringify(this.ContactPhone)}` +
            ',' +
            `"CreatedBy": ${JSON.stringify(this.CreatedBy)}` +
            ',' +
            `"Date": ${'"' + formatDateIso(this.Date).substring(0, 10) + '"'}` +
            ',' +
            `"DisciplineName": ${JSON.stringify(this.DisciplineName)}` +
            ',' +
            `"EntityState": ${unref(this.__selfEntityState) == 'Unchanged' ? 0 : unref(this.__selfEntityState) == 'Deleted' ? 1 : unref(this.__selfEntityState) == 'Modified' ? 2 : unref(this.__selfEntityState) == 'Added' ? 3 : 0}` +
            ',' +
            `"EventID": ${JSON.stringify(this.EventID)}` +
            ',' +
            `"FederationName": ${JSON.stringify(this.FederationName)}` +
            ',' +
            `"FirstStart": ${'"' + formatDateIso(this.FirstStart) + '"'}` +
            ',' +
            `"HasResult": ${JSON.stringify(this.HasResult)}` +
            ',' +
            `"Name": ${JSON.stringify(this.Name)}` +
            ',' +
            `"NumContestants": ${JSON.stringify(this.NumContestants)}` +
            ',' +
            `"OrganizerName": ${JSON.stringify(this.OrganizerName)}` +
            ',' +
            `"SportlevelName": ${JSON.stringify(this.SportlevelName)}` +
            ',' +
            `"SportName": ${JSON.stringify(this.SportName)}` +
            ',' +
            `"ChangedColumns": ${unref(this.__selfEntityState) !== 'Modified' ? 'null' : JSON.stringify(this.__entityStateHandler.getChangedPropertiesArray())}` +
            '}'
        );
    }

    /**
     * Sets the entity state to Deleted
     */
    public Delete() {
        if (this.__selfEntityState === 'Added') {
            throw new Error('Cannot delete an object that is in Added state. It has not been saved yet.');
        }
        this.__selfEntityState = 'Deleted';
        this.__entityStateHandler.entityStateChanged(this.__selfEntityState);
    }

    /**
     * Undeletes the object by setting resetting the EntityState
     *
     * If the object has modifications, the new state will be 'Modified', otherwise 'Unchanged'
     */
    public DeleteUndo() {
        this.__selfEntityState = 'Unchanged';
        this.__entityStateHandler.entityStateChanged(this.__selfEntityState);
    }

    /**
     * Sets the entity state to Added
     *
     * Also sets the ID to the supplied ID. If no id parameter supplied, the previous id is kept
     *
     * Note: This does not do anything with the children, so use with caution
     *
     * The use case of this is to make copies of existing objects as new objects without having to fill every field.
     */
    public SetEntityStateAsAddedAndResetId(id?: number) {
        if (this.EntityState !== 'Unchanged') {
            throw new Error('Changing a modified object to Added is not supported.');
        }
        this.__selfEntityState = 'Added';
        if (id !== undefined) {
            this.__id = id;
        }
        this.__entityStateHandler.entityStateChanged(this.__selfEntityState);
    }

    /**
     * Creates an instance of EventinfoSearchlistItem from the supplied values.
     * Missing values have default values
     * @param values The values you want to set
     * @returns An object instance with the supplied values set
     */
    public static FromJsValues(values: {
        ID?: number;
        ContactEmail?: string | null;
        ContactPerson?: string | null;
        ContactPhone?: string | null;
        CreatedBy?: string | null;
        Date?: Date;
        DisciplineName?: string | null;
        FederationName?: string | null;
        FirstStart?: Date;
        HasResult?: boolean;
        Name?: string | null;
        NumContestants?: number;
        OrganizerName?: string | null;
        SportlevelName?: string | null;
        SportName?: string | null;
        EntityState?: 'Added' | 'Unchanged';
    }): EventinfoSearchlistItem {
        return new EventinfoSearchlistItem(values);
    }

    public static _FromServerJson(json: any): EventinfoSearchlistItem {
        return new EventinfoSearchlistItem({
            ID: json.ID,
            ContactEmail: json.ContactEmail ?? '',
            ContactPerson: json.ContactPerson ?? '',
            ContactPhone: json.ContactPhone ?? '',
            CreatedBy: json.CreatedBy ?? '',
            Date: json.Date instanceof Date ? json.Date : new Date(json.Date + ' 00:00:00'),
            DisciplineName: json.DisciplineName ?? '',
            EventID: json.EventID,
            FederationName: json.FederationName ?? '',
            FirstStart: json.FirstStart instanceof Date ? json.FirstStart : new Date(json.FirstStart),
            HasResult: json.HasResult,
            Name: json.Name ?? '',
            NumContestants: json.NumContestants,
            OrganizerName: json.OrganizerName ?? '',
            SportlevelName: json.SportlevelName ?? '',
            SportName: json.SportName ?? '',
            EntityState: 'Unchanged',
        });
    }

    /**
     * Creates an instance of EventinfoSearchlistItem from another instance of EventinfoSearchlistItem.
     * @param other The EventinfoSearchlistItem you want to clone
     * @returns A cloned EventinfoSearchlistItem
     */
    public static FromOther(other: EventinfoSearchlistItem): EventinfoSearchlistItem {
        if (other.EntityState !== 'Unchanged') {
            throw new Error('Cloning an object with EntityState other than Unchanged is not supported');
        }
        return EventinfoSearchlistItem._FromServerJson(JSON.parse(other.serialize()));
    }

    /**
     * Deepclones the object.
     */
    public DeepClone() {
        return EventinfoSearchlistItem.FromOther(this);
    }

    /**
     * IsPropertyChanged.
     */
    public IsPropertyChanged(
        ...props: (
            | 'ID'
            | 'ContactEmail'
            | 'ContactPerson'
            | 'ContactPhone'
            | 'CreatedBy'
            | 'Date'
            | 'DisciplineName'
            | 'EventID'
            | 'FederationName'
            | 'FirstStart'
            | 'HasResult'
            | 'Name'
            | 'NumContestants'
            | 'OrganizerName'
            | 'SportlevelName'
            | 'SportName'
        )[]
    ) {
        return this.__entityStateHandler.isPropertyChanged(...props);
    }
    static NAME_OF_CLASS = 'EventinfoSearchlistItem';
}
